import { Fragment, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon, PlusIcon, ShieldCheckIcon, TrashIcon, PencilIcon } from "@heroicons/react/24/outline";
import { fetchInsurers } from "../../store/thunks/insurerThunk";
import { fetchClientById, updateClient } from "../../store/thunks/clientsThunk";
import { toast } from "react-toastify";
import axios from "axios";
import { generateConfig } from "../../utils/api";

const emptyPolicy = {
  insurer: "",
  memberID: "",
  groupNumber: "",
  groupName: "",
  planName: "",
  priority: "primary",
  subscriber: {
    firstName: "",
    lastName: "",
    dateOfBirth: "",
    gender: "",
    relationshipToPatient: "Self",
  },
  effectiveDate: "",
  terminationDate: "",
  isActive: true,
  copay: "",
  deductible: { annual: "", met: "" },
  coinsurance: "",
  notes: "",
};

export default function ClientInsurance({ client }) {
  const dispatch = useDispatch();
  const { insurers } = useSelector((state) => state.insurer);
  const token = useSelector((state) => state.auth.token);
  const currentUser = useSelector((state) => state.auth.user);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [formData, setFormData] = useState({ ...emptyPolicy });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    dispatch(fetchInsurers());
  }, [dispatch]);

  const policies = client?.insurancePolicies || [];

  const handleAddNew = () => {
    setEditingIndex(null);
    setFormData({ ...emptyPolicy });
    setIsModalOpen(true);
  };

  const handleEdit = (index) => {
    const policy = policies[index];
    setEditingIndex(index);
    setFormData({
      ...policy,
      insurer: policy.insurer?._id || policy.insurer || "",
      effectiveDate: policy.effectiveDate ? new Date(policy.effectiveDate).toISOString().split("T")[0] : "",
      terminationDate: policy.terminationDate ? new Date(policy.terminationDate).toISOString().split("T")[0] : "",
      subscriber: {
        ...emptyPolicy.subscriber,
        ...policy.subscriber,
        dateOfBirth: policy.subscriber?.dateOfBirth ? new Date(policy.subscriber.dateOfBirth).toISOString().split("T")[0] : "",
      },
      deductible: { ...emptyPolicy.deductible, ...policy.deductible },
    });
    setIsModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const { baseURL, config } = generateConfig({ token });
      const payload = { ...formData };

      // Convert empty strings to undefined for numbers
      if (payload.copay === "") payload.copay = undefined;
      if (payload.coinsurance === "") payload.coinsurance = undefined;
      if (payload.deductible?.annual === "") payload.deductible.annual = undefined;
      if (payload.deductible?.met === "") payload.deductible.met = undefined;

      if (editingIndex !== null) {
        await axios.put(
          `${baseURL}/api/clients/${client._id}/insurance/${editingIndex}`,
          payload,
          config
        );
        toast.success("Insurance policy updated");
      } else {
        await axios.post(
          `${baseURL}/api/clients/${client._id}/insurance`,
          payload,
          config
        );
        toast.success("Insurance policy added");
      }

      dispatch(fetchClientById(client._id));
      setIsModalOpen(false);
    } catch (error) {
      console.error("[ClientInsurance] Error saving policy:", error);
      toast.error("Failed to save insurance policy");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (index) => {
    if (!window.confirm("Remove this insurance policy?")) return;

    try {
      const { baseURL, config } = generateConfig({ token });
      await axios.delete(`${baseURL}/api/clients/${client._id}/insurance/${index}`, config);
      toast.success("Insurance policy removed");
      dispatch(fetchClientById(client._id));
    } catch (error) {
      console.error("[ClientInsurance] Error deleting policy:", error);
      toast.error("Failed to remove insurance policy");
    }
  };

  const handleVerify = async (index) => {
    try {
      const { baseURL, config } = generateConfig({ token });
      await axios.post(
        `${baseURL}/api/clients/${client._id}/insurance/${index}/verify`,
        {
          verifiedBy: currentUser._id,
          eligibilityStatus: "active",
          notes: "Manual verification",
        },
        config
      );
      toast.success("Verification recorded");
      dispatch(fetchClientById(client._id));
    } catch (error) {
      console.error("[ClientInsurance] Error verifying:", error);
      toast.error("Failed to record verification");
    }
  };

  const getInsurerName = (insurerId) => {
    const insurer = insurers.find((i) => i._id === (insurerId?._id || insurerId));
    return insurer?.name || "Unknown Insurer";
  };

  const priorityColors = {
    primary: "bg-blue-100 text-blue-800",
    secondary: "bg-purple-100 text-purple-800",
    tertiary: "bg-gray-100 text-gray-800",
  };

  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const updateSubscriber = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      subscriber: { ...prev.subscriber, [field]: value },
    }));
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-900">Insurance Policies</h3>
        <button
          onClick={handleAddNew}
          className="inline-flex items-center rounded-sm bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-indigo-500"
        >
          <PlusIcon className="h-4 w-4 mr-1" />
          Add Policy
        </button>
      </div>

      {policies.length === 0 ? (
        <p className="text-sm text-gray-500 py-4">No insurance policies on file.</p>
      ) : (
        <div className="space-y-3">
          {policies.map((policy, index) => (
            <div key={index} className="border border-gray-200 rounded-sm p-4">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <h4 className="text-sm font-semibold text-gray-900">
                      {getInsurerName(policy.insurer)}
                    </h4>
                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${priorityColors[policy.priority] || priorityColors.primary}`}>
                      {policy.priority}
                    </span>
                    {!policy.isActive && (
                      <span className="inline-flex items-center rounded-full bg-red-100 text-red-800 px-2 py-0.5 text-xs font-medium">
                        Inactive
                      </span>
                    )}
                  </div>
                  <div className="mt-1 text-sm text-gray-600 space-y-0.5">
                    {policy.memberID && <p>Member ID: {policy.memberID}</p>}
                    {policy.groupNumber && <p>Group: {policy.groupNumber}</p>}
                    {policy.planName && <p>Plan: {policy.planName}</p>}
                    {policy.copay && <p>Copay: ${policy.copay}</p>}
                    {policy.subscriber?.relationshipToPatient && policy.subscriber.relationshipToPatient !== "Self" && (
                      <p>Subscriber: {policy.subscriber.firstName} {policy.subscriber.lastName} ({policy.subscriber.relationshipToPatient})</p>
                    )}
                  </div>
                  {policy.verificationHistory?.length > 0 && (
                    <p className="mt-1 text-xs text-green-600">
                      Last verified: {new Date(policy.verificationHistory[policy.verificationHistory.length - 1].verifiedDate).toLocaleDateString()}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => handleVerify(index)} title="Verify eligibility"
                    className="p-1 text-green-600 hover:text-green-800">
                    <ShieldCheckIcon className="h-5 w-5" />
                  </button>
                  <button onClick={() => handleEdit(index)} title="Edit"
                    className="p-1 text-gray-600 hover:text-gray-800">
                    <PencilIcon className="h-5 w-5" />
                  </button>
                  <button onClick={() => handleDelete(index)} title="Remove"
                    className="p-1 text-red-600 hover:text-red-800">
                    <TrashIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Insurance Modal */}
      <Transition.Root show={isModalOpen} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={() => setIsModalOpen(false)}>
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Dialog.Panel className="relative transform overflow-hidden rounded-sm bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl sm:p-6">
                <div className="absolute right-0 top-0 pr-4 pt-4">
                  <button type="button" onClick={() => setIsModalOpen(false)}
                    className="rounded-sm bg-white text-gray-400 hover:text-gray-500">
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <Dialog.Title className="text-lg font-semibold text-gray-900 mb-4">
                  {editingIndex !== null ? "Edit Insurance Policy" : "Add Insurance Policy"}
                </Dialog.Title>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Insurer *</label>
                      <select required value={formData.insurer}
                        onChange={(e) => updateField("insurer", e.target.value)}
                        className="mt-1 block w-full rounded-sm border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm">
                        <option value="">Select insurer...</option>
                        {insurers.map((ins) => (
                          <option key={ins._id} value={ins._id}>{ins.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Priority</label>
                      <select value={formData.priority}
                        onChange={(e) => updateField("priority", e.target.value)}
                        className="mt-1 block w-full rounded-sm border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm">
                        <option value="primary">Primary</option>
                        <option value="secondary">Secondary</option>
                        <option value="tertiary">Tertiary</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Member ID *</label>
                      <input type="text" required value={formData.memberID}
                        onChange={(e) => updateField("memberID", e.target.value)}
                        className="mt-1 block w-full rounded-sm border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Group Number</label>
                      <input type="text" value={formData.groupNumber}
                        onChange={(e) => updateField("groupNumber", e.target.value)}
                        className="mt-1 block w-full rounded-sm border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Group Name</label>
                      <input type="text" value={formData.groupName}
                        onChange={(e) => updateField("groupName", e.target.value)}
                        className="mt-1 block w-full rounded-sm border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Plan Name</label>
                      <input type="text" value={formData.planName}
                        onChange={(e) => updateField("planName", e.target.value)}
                        className="mt-1 block w-full rounded-sm border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Effective Date</label>
                      <input type="date" value={formData.effectiveDate}
                        onChange={(e) => updateField("effectiveDate", e.target.value)}
                        className="mt-1 block w-full rounded-sm border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Termination Date</label>
                      <input type="date" value={formData.terminationDate}
                        onChange={(e) => updateField("terminationDate", e.target.value)}
                        className="mt-1 block w-full rounded-sm border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" />
                    </div>
                  </div>

                  {/* Financial */}
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Copay ($)</label>
                      <input type="number" step="0.01" value={formData.copay}
                        onChange={(e) => updateField("copay", e.target.value)}
                        className="mt-1 block w-full rounded-sm border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Annual Deductible</label>
                      <input type="number" step="0.01" value={formData.deductible?.annual || ""}
                        onChange={(e) => setFormData(prev => ({ ...prev, deductible: { ...prev.deductible, annual: e.target.value }}))}
                        className="mt-1 block w-full rounded-sm border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Coinsurance (%)</label>
                      <input type="number" step="0.01" max="100" value={formData.coinsurance}
                        onChange={(e) => updateField("coinsurance", e.target.value)}
                        placeholder="e.g., 20 for 20%"
                        className="mt-1 block w-full rounded-sm border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" />
                    </div>
                  </div>

                  {/* Subscriber Info */}
                  <div className="border-t border-gray-200 pt-3">
                    <h4 className="text-sm font-medium text-gray-900 mb-2">Subscriber Information</h4>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Relationship to Patient</label>
                        <select value={formData.subscriber.relationshipToPatient}
                          onChange={(e) => updateSubscriber("relationshipToPatient", e.target.value)}
                          className="mt-1 block w-full rounded-sm border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm">
                          <option value="Self">Self</option>
                          <option value="Spouse">Spouse</option>
                          <option value="Child">Child</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>
                      {formData.subscriber.relationshipToPatient !== "Self" && (
                        <>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Subscriber First Name</label>
                            <input type="text" value={formData.subscriber.firstName}
                              onChange={(e) => updateSubscriber("firstName", e.target.value)}
                              className="mt-1 block w-full rounded-sm border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Subscriber Last Name</label>
                            <input type="text" value={formData.subscriber.lastName}
                              onChange={(e) => updateSubscriber("lastName", e.target.value)}
                              className="mt-1 block w-full rounded-sm border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Subscriber DOB</label>
                            <input type="date" value={formData.subscriber.dateOfBirth}
                              onChange={(e) => updateSubscriber("dateOfBirth", e.target.value)}
                              className="mt-1 block w-full rounded-sm border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Subscriber Gender</label>
                            <select value={formData.subscriber.gender}
                              onChange={(e) => updateSubscriber("gender", e.target.value)}
                              className="mt-1 block w-full rounded-sm border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm">
                              <option value="">Select...</option>
                              <option value="M">Male</option>
                              <option value="F">Female</option>
                            </select>
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Notes</label>
                    <textarea value={formData.notes}
                      onChange={(e) => updateField("notes", e.target.value)}
                      rows="2"
                      className="mt-1 block w-full rounded-sm border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" />
                  </div>

                  <div className="flex items-center">
                    <input type="checkbox" checked={formData.isActive}
                      onChange={(e) => updateField("isActive", e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 mr-2" />
                    <label className="text-sm font-medium text-gray-700">Active</label>
                  </div>

                  <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                    <button type="submit" disabled={saving}
                      className="inline-flex w-full justify-center rounded-sm bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:opacity-50 sm:col-start-2">
                      {saving ? "Saving..." : editingIndex !== null ? "Update" : "Add Policy"}
                    </button>
                    <button type="button" onClick={() => setIsModalOpen(false)}
                      className="mt-3 inline-flex w-full justify-center rounded-sm bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:col-start-1 sm:mt-0">
                      Cancel
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </div>
  );
}
